import { useCallback, useMemo } from 'react';
import { useGetLeadStageQualificationValidator } from './useGetLeadStageQualificationValidator';
import { useLeadStageModal } from './useLeadStageModal';
import { LEAD_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { getPipelinePropertyName, getPipelineStagePropertyName } from '../../utils/pipelineUtils';
export const useLeadStageChangeValidation = ({
  onChange,
  onApply,
  onCancel,
  objectTypeId,
  objectId,
  getAdditionalPropertyValue
}) => {
  const {
    loading,
    stageChangeValidator
  } = useGetLeadStageQualificationValidator({
    objectTypeId
  });
  const pipelineStagePropertyName = getPipelineStagePropertyName(objectTypeId);
  const pipelinePropertyName = getPipelinePropertyName(objectTypeId);
  const extraProperties = useMemo(() => {
    if (!objectId || objectTypeId !== LEAD_TYPE_ID) {
      return {};
    }
    return {
      originPipelineId: getAdditionalPropertyValue(pipelinePropertyName, objectId, objectTypeId),
      primaryContactId: getAdditionalPropertyValue('hs_primary_contact_id', objectId, objectTypeId),
      primaryCompanyId: getAdditionalPropertyValue('hs_primary_company_id', objectId, objectTypeId),
      leadOwner: getAdditionalPropertyValue('hubspot_owner_id', objectId, objectTypeId),
      leadName: getAdditionalPropertyValue('hs_lead_name', objectId, objectTypeId),
      disqualificationReason: getAdditionalPropertyValue('hs_lead_disqualification_reason', objectId, objectTypeId),
      disqualificationNote: getAdditionalPropertyValue('hs_lead_disqualification_note', objectId, objectTypeId)
    };
  }, [objectId, objectTypeId, getAdditionalPropertyValue, pipelinePropertyName]);
  const {
    stageValidationModal,
    openModal,
    closeModal
  } = useLeadStageModal();
  const handleValidation = useCallback((validationRequest, pendingChanges) => {
    const validationResult = stageChangeValidator(validationRequest);
    if (validationResult.isValid || !objectId) {
      // Otherwise continue sending values back to host
      onChange(pendingChanges);
      return;
    }

    // If we're aware that specific validation codes should warrant a confirmation modal we fire an open modal action here.
    // The modal needs to propagate the original onChange callback once onConfirm is called so we include the callback as part of the dispatched modal data.
    switch (validationResult.requirement) {
      case 'DEAL_CREATION_REQUIRED':
        {
          openModal({
            modal: 'DEAL_CREATE',
            objectId,
            extraProperties,
            onConfirm: () => {
              onChange(pendingChanges);
              onApply();
              closeModal();
            },
            onCancel: () => {
              onCancel();
              closeModal();
            }
          });
          return;
        }
      case 'RE_ATTEMPTING_LEAD_REQUIRED':
      case 'FOLLOW_UP_TASK_OPTIONAL':
      case 'RE_ATTEMPTING_LEAD_AND_TASK_REQUIRED':
        {
          var _ref, _pendingChanges$hs_le, _ref2, _pendingChanges$hs_le2;
          openModal({
            modal: validationResult.requirement,
            objectId,
            extraProperties: Object.assign({}, extraProperties, {
              disqualificationReason: (_ref = (_pendingChanges$hs_le = pendingChanges === null || pendingChanges === void 0 ? void 0 : pendingChanges['hs_lead_disqualification_reason']) !== null && _pendingChanges$hs_le !== void 0 ? _pendingChanges$hs_le : extraProperties.disqualificationReason) !== null && _ref !== void 0 ? _ref : '',
              disqualificationNote: (_ref2 = (_pendingChanges$hs_le2 = pendingChanges === null || pendingChanges === void 0 ? void 0 : pendingChanges['hs_lead_disqualification_note']) !== null && _pendingChanges$hs_le2 !== void 0 ? _pendingChanges$hs_le2 : extraProperties.disqualificationNote) !== null && _ref2 !== void 0 ? _ref2 : ''
            }),
            onConfirm: () => {
              onChange(pendingChanges);
              onApply();
              closeModal();
            },
            onCancel: () => {
              onCancel();
              closeModal();
            }
          });
          return;
        }
      default:
        {
          return validationResult.requirement;
        }
    }
  }, [stageChangeValidator, objectId, onChange, openModal, extraProperties, onApply, closeModal, onCancel]);
  const onChangeWithLeadStageValidation = useCallback(changes => {
    // We don't want this validation triggering for other input modes such as bulk actions
    if (!objectId || objectTypeId !== LEAD_TYPE_ID) {
      onChange(changes);
      return;
    }
    const stageId = changes[pipelineStagePropertyName];

    // Check local changes first, then fallback to host app's value to grab the pipeline
    const pipelineId = changes[pipelinePropertyName] || extraProperties.originPipelineId;
    const targetDisqualificationReason = changes['hs_lead_disqualification_reason'] || extraProperties.disqualificationReason;

    // If stage or pipeline isn't part of the property change, there is no validation needed.
    if (!stageId || !pipelineId) {
      onChange(changes);
      return;
    }
    handleValidation({
      targetPipelineId: pipelineId,
      targetStageId: stageId,
      targetDisqualificationReason
    }, changes);
  }, [objectId, objectTypeId, pipelineStagePropertyName, pipelinePropertyName, extraProperties.originPipelineId, extraProperties.disqualificationReason, handleValidation, onChange]);
  return useMemo(() => ({
    leadStageChangeValidatorLoading: objectTypeId === LEAD_TYPE_ID && loading,
    leadStageValidationModal: stageValidationModal,
    onChangeWithLeadStageValidation: objectId && objectTypeId === LEAD_TYPE_ID ? onChangeWithLeadStageValidation : onChange
  }), [objectId, objectTypeId, loading, stageValidationModal, onChangeWithLeadStageValidation, onChange]);
};